import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import {
    AlertCircle,
    ArrowRight,
    Award,
    BellRing,
    CalendarDays,
    ChevronDown,
    CheckCircle2,
    ClipboardList,
    Clock3,
    Factory,
    FileText,
    Gauge,
    ImagePlus,
    IndianRupee,
    Loader2,
    Package,
    Plus,
    ReceiptText,
    ShieldAlert,
    TimerReset,
    TrendingUp,
    Truck,
    UserRoundCheck,
    WalletCards,
    Wrench,
    X,
} from "lucide-react";

import api from "../../api/axios";
import { useAuth } from "../../context/AuthContext";
import SalarySlipModal from "../../components/employee/SalarySlipModal";
import {
    formatIndiaTime,
    getAutoCheckoutDisplayTime,
    getAutoCheckoutEndMs,
    isAutoCheckoutRecord,
} from "../../utils/attendanceTime";

const getDashboardCacheKey = (employee) => {
    const id = employee?._id || employee?.id;
    return id ? `employee_dashboard_summary_${id}` : null;
};

const readDashboardCache = (employee) => {
    const cacheKey = getDashboardCacheKey(employee);
    if (!cacheKey) return null;

    try {
        const cached = JSON.parse(localStorage.getItem(cacheKey) || "null");
        if (!cached?.data) return null;
        return cached.data;
    } catch {
        localStorage.removeItem(cacheKey);
        return null;
    }
};

const readAnyDashboardCache = () => {
    try {
        const keys = Object.keys(localStorage).filter((key) => key.startsWith("employee_dashboard_summary_"));
        let latest = null;
        keys.forEach((key) => {
            const cached = JSON.parse(localStorage.getItem(key) || "null");
            if (!cached?.data) return;
            if (!latest || cached.savedAt > latest.savedAt) latest = cached;
        });
        return latest?.data || null;
    } catch {
        return null;
    }
};

const getHrMonitoringCacheKey = (employee) => {
    const id = employee?._id || employee?.id;
    return id ? `employee_hr_monitoring_v3_${id}` : "employee_hr_monitoring_v3_latest";
};

const clearHrMonitoringCache = (employee) => {
    try {
        localStorage.removeItem(getHrMonitoringCacheKey(employee));
        localStorage.removeItem("employee_hr_monitoring_v3_latest");
    } catch {
        // localStorage can be unavailable in private or restricted browser modes.
    }
};

const normalizeTeam = (team = "") => {
    const value = String(team || "").trim().toLowerCase().replace(/[\s_-]+/g, " ");
    if (value === "tele marketing" || value === "telemarketing") return "telemarketing";
    if (value === "administrator") return "admin";
    return value;
};
const isManufacturingTeam = (team = "") => normalizeTeam(team).includes("manufactur");
const isHrTeam = (employee = {}) => normalizeTeam(employee?.team) === "hr" || normalizeTeam(employee?.role) === "hr";
const isFinanceTeam = (employee = {}) => normalizeTeam(employee?.team) === "finance" || normalizeTeam(employee?.role) === "finance";
const FinanceDashboard = React.lazy(() => import("./FinanceDashboard"));
const quotationTargets = [
    { value: "sales team", label: "Sales Team" },
    { value: "telemarketing", label: "Telemarketing" },
    { value: "admin", label: "Admin" },
];
const emptyQuotationForm = {
    title: "",
    customerName: "",
    quotationNo: "",
    amount: "",
    dueDate: "",
    details: "",
    proofPhoto: null,
    targetTeams: ["sales team"],
};
const emptyWorkOrderForm = {
    workOrderNo: "",
    customerName: "",
    product: "",
    priority: "normal",
    status: "planned",
    assignedLine: "",
    assignedOwner: "",
    dueDate: "",
    plannedQty: "",
    producedQty: "",
    rejectedQty: "",
    material: { status: "ready", notes: "" },
    machine: { name: "", status: "idle", downtimeMinutes: "", issue: "" },
    quality: { status: "not_started", passedQty: "", reworkQty: "", remarks: "" },
    dispatch: { status: "not_ready", date: "", details: "" },
    maintenanceRequest: { status: "none", issue: "" },
    supervisorApproval: { status: "pending", comment: "" },
    handoverNotes: "",
    delayReason: "",
};
const formatQuotationStatus = (status = "") => status.replace("-", " ");
const formatTeamLabel = (team = "") => {
    const normalized = String(team || "").trim();
    if (!normalized) return "";
    if (normalized.toLowerCase() === "hr") return "HR";
    return normalized
        .split(/\s+/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
};
const formatRoleLabel = (role = "") => {
    const value = String(role || "").trim();
    if (!value) return "";
    return value
        .split(/\s+/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
};
const formatQueueStatus = (status = "") => {
    const normalized = String(status || "").trim().toLowerCase();
    if (normalized === "pending_lead") return "Waiting for Lead";
    if (normalized === "pending") return "Pending";
    if (normalized === "open") return "Open";
    if (normalized === "approved") return "Approved";
    if (normalized === "rejected") return "Rejected";
    if (normalized === "resolved") return "Resolved";
    if (normalized === "dismissed") return "Dismissed";
    return status || "Open";
};
const isApprovedQueueStatus = (status = "") => ["approved", "resolved", "accepted", "completed"].includes(String(status || "").trim().toLowerCase());
const isRejectedQueueStatus = (status = "") => ["rejected", "dismissed"].includes(String(status || "").trim().toLowerCase());
const formatMoney = (amount) =>
    Number(amount || 0).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });

const getDateKey = (value = new Date()) => {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const formatShortDate = (value = "") => {
    if (!value) return "";
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

const formatReasonText = (value = "") => {
    const text = String(value || "").replace(/\s+/g, " ").trim();
    if (!text) return "";

    return text
        .replace(/\b(Subject\s*:)/gi, "\n$1 ")
        .replace(/\s+\b(Hi|Hello|Dear)\b(?=\s+[A-Z])/g, "\n\n$1")
        .replace(/\s+\b(Thank you|Thanks|Regards|Sincerely)\b/gi, "\n\n$1")
        .replace(/\s+\b(Kindly|Please)\b(?=\s)/gi, "\n\n$1")
        .replace(/^\n+/, "")
        .trim();
};

const getQueueDateKey = (item = {}) => {
    const value = item.date || item.reviewedAt || item.createdAt;
    return getDateKey(value || new Date());
};

const groupQueueItemsByDate = (items = []) => {
    const groups = items.reduce((acc, item) => {
        const dateKey = getQueueDateKey(item);
        if (!acc[dateKey]) acc[dateKey] = [];
        acc[dateKey].push(item);
        return acc;
    }, {});

    return Object.entries(groups)
        .sort(([dateA], [dateB]) => new Date(dateB) - new Date(dateA))
        .map(([date, groupItems]) => ({ date, items: groupItems }));
};

const hrQueueEmptyMessages = {
    grievances: "No open complaints or grievances",
    requests: "No open employee requests",
    leaves: "No leave requests",
    expenses: "No expense claims",
    attendanceCorrections: "No correction requests",
    payslipIssues: "No payslip issues",
    offboarding: "No offboarding requests",
};

const formatDurationSeconds = (totalSeconds = 0) => {
    const safeSeconds = Math.max(0, Math.floor(Number(totalSeconds) || 0));
    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const seconds = safeSeconds % 60;

    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
};

const getBreakDurationSeconds = (record, nowMs) => {
    const breaksSeconds = (record?.breaks || []).reduce((total, item) => {
        if (!item?.startTime) return total;

        const startMs = new Date(item.startTime).getTime();
        const endMs = item.endTime ? new Date(item.endTime).getTime() : nowMs;

        if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs <= startMs) return total;
        return total + Math.floor((endMs - startMs) / 1000);
    }, 0);

    if (breaksSeconds > 0) return breaksSeconds;

    if (record?.breakStart && record?.breakEnd) {
        const startMs = new Date(record.breakStart).getTime();
        const endMs = new Date(record.breakEnd).getTime();
        if (!Number.isNaN(startMs) && !Number.isNaN(endMs) && endMs > startMs) {
            return Math.floor((endMs - startMs) / 1000);
        }
    }

    return 0;
};

const getWorkDurationSeconds = (record, nowMs) => {
    if (!record?.checkIn) return 0;

    const checkInMs = new Date(record.checkIn).getTime();
    const autoCheckout = isAutoCheckoutRecord(record);
    const endMs = autoCheckout
        ? getAutoCheckoutEndMs(record)
        : record.checkOut ? new Date(record.checkOut).getTime() : nowMs;

    if (!Number.isNaN(checkInMs) && !Number.isNaN(endMs) && endMs > checkInMs) {
        const durationRecord = autoCheckout
            ? { ...record, checkOut: new Date(endMs).toISOString() }
            : record;
        return Math.max(0, Math.floor((endMs - checkInMs) / 1000) - getBreakDurationSeconds(durationRecord, endMs));
    }

    if (record.duration !== undefined) {
        return Number(record.duration || 0) * 60;
    }

    return 0;
};

const Dashboard = () => {
    const { employee } = useAuth();
    const navigate = useNavigate();
    const cachedSummary = useMemo(() => readDashboardCache(employee) || readAnyDashboardCache(), [employee]);

    const [attendanceStatus, setAttendanceStatus] = useState(cachedSummary?.attendanceStatus || { status: "not_checked_in" });
    const [tasks, setTasks] = useState(cachedSummary?.tasks || []);
    const [dashboardTaskStats, setDashboardTaskStats] = useState(cachedSummary?.taskStats || null);
    const [announcements, setAnnouncements] = useState(cachedSummary?.announcements || []);
    const [unreadCount, setUnreadCount] = useState(cachedSummary?.unreadCount || 0);
    const [todayEOD, setTodayEOD] = useState(cachedSummary?.todayEOD || null);
    const [recognitionBadges, setRecognitionBadges] = useState(cachedSummary?.recognitionBadges || []);
    const [, setLoading] = useState(false);
    const [loadError, setLoadError] = useState("");
    const [nowMs, setNowMs] = useState(Date.now());
    const [showSalarySlip, setShowSalarySlip] = useState(false);
    const [selectedBadge, setSelectedBadge] = useState(null);
    const [quotations, setQuotations] = useState([]);
    const [quotationForm, setQuotationForm] = useState(emptyQuotationForm);
    const [quotationLoading, setQuotationLoading] = useState(false);
    const [quotationSaving, setQuotationSaving] = useState(false);
    const [quotationMessage, setQuotationMessage] = useState("");
    const [isQuotationModalOpen, setIsQuotationModalOpen] = useState(false);
    const [selectedQuotation, setSelectedQuotation] = useState(null);
    const [manufacturingData, setManufacturingData] = useState({ summary: {}, workOrders: [], alerts: [], trends: [] });
    const [manufacturingLoading, setManufacturingLoading] = useState(false);
    const [manufacturingMessage, setManufacturingMessage] = useState("");
    const [isWorkOrderModalOpen, setIsWorkOrderModalOpen] = useState(false);
    const [workOrderForm, setWorkOrderForm] = useState(emptyWorkOrderForm);
    const [workOrderSaving, setWorkOrderSaving] = useState(false);
    const isManufacturing = isManufacturingTeam(employee?.team);
    const isHr = isHrTeam(employee);
    const isFinance = isFinanceTeam(employee);
    const [hrMonitoring, setHrMonitoring] = useState(null);
    const [hrLoading, setHrLoading] = useState(false);
    const [hrMessage, setHrMessage] = useState("");
    const [hrUpdatedAt, setHrUpdatedAt] = useState("");
    const [selectedHrEmployee, setSelectedHrEmployee] = useState(null);
    const [showHrEmployeeDirectory, setShowHrEmployeeDirectory] = useState(false);
    const [missingEodMode, setMissingEodMode] = useState("today");
    const [missingEodStartDate, setMissingEodStartDate] = useState(getDateKey());
    const [missingEodEndDate, setMissingEodEndDate] = useState(getDateKey());
    const [activeHrQueue, setActiveHrQueue] = useState("grievances");
    const [hrQueueMessage, setHrQueueMessage] = useState("");

    useEffect(() => {
        if (!cachedSummary) return;
        setAttendanceStatus(cachedSummary.attendanceStatus || { status: "not_checked_in" });
        setTasks(cachedSummary.tasks || []);
        setDashboardTaskStats(cachedSummary.taskStats || null);
        setAnnouncements(cachedSummary.announcements || []);
        setUnreadCount(cachedSummary.unreadCount || 0);
        setTodayEOD(cachedSummary.todayEOD || null);
        setRecognitionBadges(cachedSummary.recognitionBadges || []);
    }, [cachedSummary]);

    const formatTime = (time) => {
        return formatIndiaTime(time, "-");
    };

    const AutoCheckoutDisplay = ({ time }) => (
        <span className="inline-flex max-w-full flex-wrap items-center gap-1.5">
            <span className="whitespace-nowrap">{time}</span>
            <span className="whitespace-nowrap rounded-full bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-black uppercase leading-none text-amber-700 ring-1 ring-amber-500/20 dark:text-amber-300">
                Auto checked out
            </span>
        </span>
    );

    const formatCheckout = (item) => (
        isAutoCheckoutRecord(item) ? <AutoCheckoutDisplay time={getAutoCheckoutDisplayTime(item, formatTime)} /> : formatTime(item?.checkOut)
    );

    useEffect(() => {
        let cancelled = false;

        const fetchData = async () => {
            try {
                const res = await api.get("/api/employee/dashboard-summary", {
                    cache: false,
                    params: { _t: Date.now() },
                });
                const cacheKey = getDashboardCacheKey(employee);

                if (cancelled) return;
                setLoadError("");
                setAttendanceStatus(res.data.attendanceStatus);
                setTasks(res.data.tasks || []);
                setDashboardTaskStats(res.data.taskStats || null);
                setAnnouncements(res.data.announcements || []);
                setUnreadCount(res.data.unreadCount || 0);
                setTodayEOD(res.data.todayEOD || null);
                setRecognitionBadges(res.data.recognitionBadges || []);

                if (cacheKey) {
                    localStorage.setItem(cacheKey, JSON.stringify({
                        data: res.data,
                        savedAt: Date.now(),
                    }));
                }
            } catch (err) {
                if (cancelled) return;
                console.error("Failed to load employee dashboard:", err);
                setLoadError(err.response?.data?.message || "Failed to load latest dashboard data. Please refresh once.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        const refresh = () => {
            if (!document.hidden) fetchData();
        };

        fetchData();
        const refreshInterval = window.setInterval(refresh, 15000);
        const token = localStorage.getItem("employee_token");
        const socket = token
            ? io(api.defaults.baseURL || window.location.origin, {
                auth: { token },
                transports: ["polling", "websocket"],
            })
            : null;
        const onActivity = (activity = {}) => {
            if (!["task-assigned", "task-updated"].includes(activity.type)) return;
            const recipientIds = Array.isArray(activity.recipientIds) ? activity.recipientIds : [];
            const employeeId = String(employee?._id || employee?.id || "");
            if (employeeId && recipientIds.map(String).includes(employeeId)) fetchData();
        };
        socket?.on("activity", onActivity);
        window.addEventListener("focus", refresh);
        document.addEventListener("visibilitychange", refresh);
        return () => {
            cancelled = true;
            window.clearInterval(refreshInterval);
            socket?.off("activity", onActivity);
            socket?.disconnect();
            window.removeEventListener("focus", refresh);
            document.removeEventListener("visibilitychange", refresh);
        };
    }, [employee, navigate]);

    useEffect(() => {
        const interval = setInterval(() => setNowMs(Date.now()), 1000);
        return () => clearInterval(interval);
    }, []);

    const fetchQuotations = useCallback(async () => {
        if (!isManufacturing) return;
        setQuotationLoading(true);
        try {
            const res = await api.get("/api/employee/quotations");
            setQuotations(res.data || []);
        } catch (error) {
            setQuotationMessage(error.response?.data?.message || "Unable to load quotations.");
        } finally {
            setQuotationLoading(false);
        }
    }, [isManufacturing]);

    useEffect(() => {
        fetchQuotations();
    }, [fetchQuotations]);

    const fetchManufacturingDashboard = useCallback(async () => {
        if (!isManufacturing) return;
        setManufacturingLoading(true);
        try {
            const res = await api.get("/api/employee/manufacturing/dashboard");
            setManufacturingData(res.data || { summary: {}, workOrders: [], alerts: [], trends: [] });
            setManufacturingMessage("");
        } catch (error) {
            setManufacturingMessage(error.response?.data?.message || "Unable to load manufacturing dashboard.");
        } finally {
            setManufacturingLoading(false);
        }
    }, [isManufacturing]);

    useEffect(() => {
        fetchManufacturingDashboard();
    }, [fetchManufacturingDashboard]);

    const setQuotationField = (field, value) => {
        setQuotationForm((current) => ({ ...current, [field]: value }));
    };

    const setQuotationPhoto = (file) => {
        if (!file) {
            setQuotationField("proofPhoto", null);
            return;
        }
        if (!file.type.startsWith("image/")) {
            setQuotationMessage("Please upload an image file for the quotation photo.");
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            setQuotationForm((current) => ({
                ...current,
                proofPhoto: {
                    name: file.name,
                    data: reader.result,
                },
            }));
        };
        reader.readAsDataURL(file);
    };

    const toggleQuotationTarget = (team) => {
        setQuotationForm((current) => {
            const selected = current.targetTeams || [];
            const next = selected.includes(team)
                ? selected.filter((item) => item !== team)
                : [...selected, team];
            return { ...current, targetTeams: next };
        });
    };

    const submitQuotation = async (event) => {
        event.preventDefault();
        setQuotationSaving(true);
        setQuotationMessage("");
        try {
            await api.post("/api/employee/quotations", quotationForm);
            setQuotationForm(emptyQuotationForm);
            setQuotationMessage("Quotation assigned successfully.");
            setIsQuotationModalOpen(false);
            await fetchQuotations();
        } catch (error) {
            setQuotationMessage(error.response?.data?.message || "Unable to assign quotation.");
        } finally {
            setQuotationSaving(false);
        }
    };

    const setWorkOrderField = (field, value) => {
        setWorkOrderForm((current) => ({ ...current, [field]: value }));
    };

    const setWorkOrderNestedField = (section, field, value) => {
        setWorkOrderForm((current) => ({
            ...current,
            [section]: {
                ...(current[section] || {}),
                [field]: value,
            },
        }));
    };

    const submitWorkOrder = async (event) => {
        event.preventDefault();
        setWorkOrderSaving(true);
        setManufacturingMessage("");
        try {
            await api.post("/api/employee/manufacturing/work-orders", workOrderForm);
            setWorkOrderForm(emptyWorkOrderForm);
            setIsWorkOrderModalOpen(false);
            setManufacturingMessage("Work order created.");
            await fetchManufacturingDashboard();
        } catch (error) {
            setManufacturingMessage(error.response?.data?.message || "Unable to create work order.");
        } finally {
            setWorkOrderSaving(false);
        }
    };

    const quickUpdateWorkOrder = async (workOrder, patch) => {
        try {
            await api.patch(`/api/employee/manufacturing/work-orders/${workOrder._id}`, {
                ...workOrder,
                ...patch,
            });
            await fetchManufacturingDashboard();
        } catch (error) {
            setManufacturingMessage(error.response?.data?.message || "Unable to update work order.");
        }
    };

    const fetchHrMonitoring = useCallback(async () => {
        if (!isHr) return;
        setHrLoading(true);
        try {
            const dateParams = missingEodMode === "today"
                ? { date: getDateKey() }
                : {
                    startDate: missingEodStartDate || getDateKey(),
                    endDate: missingEodEndDate || missingEodStartDate || getDateKey(),
                };
            const res = await api.get("/api/employee/hr/monitoring-summary", {
                params: { limit: 8, ...dateParams, _t: Date.now() },
                cache: false,
            });
            if (!res.data?.counts || !res.data?.dashboard || !res.data?.queues) {
                throw new Error("HR monitoring response was incomplete.");
            }
            const next = res.data;
            setHrMonitoring(next);
            clearHrMonitoringCache(employee);
            setHrUpdatedAt(new Date().toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit" }));
            setHrMessage("");
        } catch (error) {
            setHrMessage(error.response?.data?.message || "Unable to sync latest HR monitoring data.");
        } finally {
            setHrLoading(false);
        }
    }, [employee, isHr, missingEodEndDate, missingEodMode, missingEodStartDate]);

    useEffect(() => {
        fetchHrMonitoring();
    }, [fetchHrMonitoring]);

    const notifyAdminForCorrection = useCallback(async (item) => {
        if (!item?._id) return;
        setHrQueueMessage("Messaging admin...");
        try {
            await api.post(`/api/employee/team/attendance-corrections/${item._id}/notify-admin`);
            setHrQueueMessage("Admin message sent for this pending correction.");
            await fetchHrMonitoring();
        } catch (error) {
            setHrQueueMessage(error.response?.data?.message || "Unable to Message admin.");
        } finally {
            window.setTimeout(() => setHrQueueMessage(""), 2600);
        }
    }, [fetchHrMonitoring]);

    const taskStats = useMemo(() => {
        if (dashboardTaskStats) {
            return {
                pending: dashboardTaskStats.pending || 0,
                inProgress: dashboardTaskStats.inProgress || 0,
                completed: dashboardTaskStats.completed || 0,
            };
        }

        return {
            pending: tasks.filter((task) => task.status === "pending").length,
            inProgress: tasks.filter((task) => task.status === "in-progress").length,
            completed: tasks.filter((task) => task.status === "completed").length,
        };
    }, [dashboardTaskStats, tasks]);

    const greeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return "Good Morning";
        if (hour < 18) return "Good Afternoon";
        return "Good Evening";
    };

    const record = attendanceStatus?.record;
    const assetCount = employee?.assignedAssets?.length || 0;
    const latestAnnouncements = announcements.slice(0, 3);
    const workDuration = formatDurationSeconds(getWorkDurationSeconds(record, nowMs));
    const workspaceLabel = String(employee?.role || "").toLowerCase().includes("lead")
        ? "Lead Workspace"
        : "Employee Workspace";

    const attendanceView = getAttendanceView(attendanceStatus, record, formatTime, formatCheckout);
    const manufacturingSummary = manufacturingData.summary || {};
    const manufacturingWorkOrders = manufacturingData.workOrders || [];
    const manufacturingTrends = manufacturingData.trends || [];

    if (isFinance) {
        return (
            <React.Suspense fallback={<main className="min-h-screen bg-[var(--bg-primary)] p-6 text-sm text-[var(--text-secondary)]">Loading finance dashboard...</main>}>
                <FinanceDashboard employee={employee} />
            </React.Suspense>
        );
    }

    if (isHr) {
        const hasHrData = Boolean(hrMonitoring);
        const counts = hrMonitoring?.counts || {};
        const queues = hrMonitoring?.queues || {};
        const hrDashboard = hrMonitoring?.dashboard || {};
        const hrSummary = hrDashboard.summary || {};
        const hrAttendance = hrDashboard.attendanceHealth || {};
        const hrPayroll = hrDashboard.payrollReadiness || {};
        const hrLifecycle = hrDashboard.lifecycle || {};
        const hrDocuments = hrDashboard.documents || {};
        const hrComplaints = hrDashboard.complaints || {};
        const hrRiskAlerts = hrDashboard.riskAlerts || [];
        const hrTeams = hrDashboard.teams || [];
        const employeeInsights = hrMonitoring?.employees || {};
        const missingEodRange = hrMonitoring?.filters?.missingEod || {};
        const hrDataLoading = hrLoading && !hasHrData;
        const queueCount = (key, fallback = 0) => (queues[key] || []).length || fallback;
        const hrQueueTabs = [
            { key: "grievances", label: "Complaints", count: queueCount("grievances", counts.grievances || 0), items: queues.grievances || [] },
            { key: "requests", label: "Requests", count: queueCount("requests", counts.requests || 0), items: queues.requests || [] },
            { key: "leaves", label: "Leaves", count: queueCount("leaves", counts.leaves || 0), items: queues.leaves || [] },
            { key: "expenses", label: "Expenses", count: queueCount("expenses", counts.expenses || 0), items: queues.expenses || [] },
            { key: "attendanceCorrections", label: "Corrections", count: queueCount("attendanceCorrections", counts.attendanceCorrections || 0), items: queues.attendanceCorrections || [] },
            { key: "payslipIssues", label: "Payslip", count: queueCount("payslipIssues", counts.payslipIssues || 0), items: queues.payslipIssues || [] },
            { key: "offboarding", label: "Offboarding", count: queueCount("offboarding", counts.offboarding || 0), items: queues.offboarding || [] },
        ];
        const selectedHrQueue = hrQueueTabs.find((item) => item.key === activeHrQueue) || hrQueueTabs[0];

        return (
            <div className="min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)]">
                <main className="employee-dashboard-premium hr-admin-dashboard">
                    <section className="premium-shell">
                        <div className="premium-hero hr-monitor-hero">
                            <div className="premium-hero-copy">
                                <span className="premium-eyebrow">HR control center</span>
                                <h1>Employee monitoring dashboard</h1>
                                <div className="premium-meta-row">
                                    <span>{employee?.name || "HR"}</span>
                                    {(!employee?.team || String(employee.team).toLowerCase() !== String(employee?.name || "HR").toLowerCase()) && (
                                        <span>{employee?.team || "HR"}</span>
                                    )}
                                    <span>{new Date().toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}</span>
                                </div>
                            </div>

                            <div className="hr-monitor-kpi-grid">
                                <HrMetric label="Employees" value={hrDataLoading ? "..." : hasHrData ? counts.employees || 0 : "-"} icon={<UserRoundCheck size={18} />} />
                                <HrMetric label="Open items" value={hrDataLoading ? "..." : hasHrData ? counts.totalOpen || 0 : "-"} icon={<ClipboardList size={18} />} />
                                <HrMetric label="Inactive" value={hrDataLoading ? "..." : hasHrData ? counts.inactiveEmployees || 0 : "-"} icon={<ShieldAlert size={18} />} />
                            </div>
                        </div>

                        {(hrLoading || hrUpdatedAt || hrMessage) && (
                            <section className="premium-card border border-[var(--border-color)] p-4 text-sm font-bold text-[var(--text-secondary)]">
                                {hrMessage || (hrLoading ? "Syncing latest HR records..." : `Latest HR sync ${hrUpdatedAt}`)}
                            </section>
                        )}

                        {!hasHrData && !hrLoading && (
                            <section className="premium-card border border-[var(--border-color)] p-4 text-sm font-bold text-[var(--text-secondary)]">
                                HR metrics are hidden until the latest employee, attendance, payroll, and complaint records load successfully.
                            </section>
                        )}

                        <section className="hr-admin-actions" aria-label="HR actions">
                            <HrActionButton icon={<UserRoundCheck size={18} />} label="Team Attendance" onClick={() => navigate("/team/attendance")} />
                            <HrActionButton icon={<CalendarDays size={18} />} label="Leave Monitor" onClick={() => navigate("/team/leave")} />
                            <HrActionButton icon={<ShieldAlert size={18} />} label="Complaints" onClick={() => navigate("/complaints?view=employee-list")} />
                            <HrActionButton icon={<FileText size={18} />} label="Offboarding" onClick={() => navigate("/offboarding?view=employee-list")} />
                            <HrActionButton icon={<BellRing size={18} />} label="Policies" onClick={() => navigate("/policies")} />
                        </section>

                        {hasHrData && (
                            <>
                                <section className="hr-monitor-advanced-grid" aria-label="Advanced HR monitoring">
                                    <HrMonitorPanel title="Today Watchlist" kicker="Daily check">
                                        <HrMonitorStat label="Present today" value={hrSummary.presentToday ?? 0} tone="good" />
                                        <HrMonitorStat label="Absent today" value={hrSummary.absentToday ?? hrPayroll.missingAttendance ?? 0} tone="danger" />
                                        <HrMonitorStat label="Late check-ins" value={hrSummary.lateCheckIns ?? hrAttendance.lateComingTrend ?? 0} tone="warn" />
                                        <HrMonitorStat label="Missing checkout" value={hrAttendance.missingCheckout ?? 0} tone="warn" />
                                        <HrMonitorStat label="Employees on leave" value={hrAttendance.leaveCount ?? 0} />
                                        <HrMonitorStat label="Open complaints" value={hrSummary.openComplaints ?? counts.grievances ?? 0} tone="danger" />
                                    </HrMonitorPanel>

                                    <HrMonitorPanel title="Employee Risk Monitor" kicker="Smart warnings">
                                        {hrRiskAlerts.length ? (
                                            <div className="hr-monitor-alert-list">
                                                {hrRiskAlerts.slice(0, 5).map((alert, index) => (
                                                    <div key={`${alert.title}-${index}`} className="hr-monitor-alert">
                                                        <strong>{alert.title}</strong>
                                                        <span>{alert.detail || alert.level || "Needs HR review"}</span>
                                                        {Array.isArray(alert.evidence) && alert.evidence.length > 0 && (
                                                            <div className="hr-monitor-alert-evidence">
                                                                {alert.evidence.slice(0, 3).map((item, evidenceIndex) => (
                                                                    <small key={`${item.date || evidenceIndex}-${item.timing || evidenceIndex}`}>
                                                                        {item.dateLabel || item.date || "Date not recorded"}
                                                                        {item.timing ? ` - ${item.timing}` : ""}
                                                                    </small>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="premium-empty-text">No major employee risk alerts.</p>
                                        )}
                                    </HrMonitorPanel>

                                    <HrMonitorPanel title="Payroll Readiness" kicker="Salary blockers">
                                        <HrMonitorStat label="Missing bank details" value={hrPayroll.missingBankDetails ?? 0} tone="danger" />
                                        <HrMonitorStat label="Missing PAN/UAN" value={hrPayroll.missingPanUan ?? 0} tone="danger" />
                                        <HrMonitorStat label="Loss of pay" value={hrPayroll.lossOfPayCount ?? 0} tone="warn" />
                                        <HrMonitorStat label="Unapproved leaves" value={hrPayroll.unapprovedLeaves ?? counts.leaves ?? 0} tone="warn" />
                                        <HrMonitorStat label="Salary slip pending" value={hrPayroll.salarySlipPending ?? 0} tone="warn" />
                                        <HrMonitorStat label="Payroll status" value={hrPayroll.payrollLockStatus || "Open"} />
                                    </HrMonitorPanel>

                                    <HrMonitorPanel title="Document Compliance" kicker="Missing docs">
                                        <HrMonitorStat label="Offer letters" value={hrDocuments.offerLetters ?? 0} />
                                        <HrMonitorStat label="Experience letters" value={hrDocuments.experienceLetters ?? 0} />
                                        <HrMonitorStat label="Relieving letters" value={hrDocuments.relievingLetters ?? 0} />
                                        <HrMonitorStat label="ID proofs" value={hrDocuments.idProofs ?? 0} />
                                        <HrMonitorStat label="Salary documents" value={hrDocuments.salaryDocuments ?? 0} />
                                        <HrMonitorStat label="Policy acknowledgements" value={hrDocuments.policyAcknowledgements ?? 0} />
                                    </HrMonitorPanel>

                                    <HrMonitorPanel title="Joiners And Exits" kicker="Lifecycle">
                                        <HrMonitorStat label="New joiners" value={hrLifecycle.newJoiners ?? hrSummary.newJoiners ?? 0} tone="good" />
                                        <HrMonitorStat label="Probation" value={hrLifecycle.probation ?? 0} />
                                        <HrMonitorStat label="Confirmed" value={hrLifecycle.confirmed ?? 0} />
                                        <HrMonitorStat label="Notice period" value={hrLifecycle.noticePeriod ?? 0} tone="warn" />
                                        <HrMonitorStat label="Ex-employees" value={hrLifecycle.exEmployees ?? counts.inactiveEmployees ?? 0} />
                                        <HrMonitorStat label="Exits this month" value={hrSummary.exitsThisMonth ?? counts.offboarding ?? 0} tone="warn" />
                                    </HrMonitorPanel>

                                    <HrMonitorPanel title="Complaint SLA" kicker="Response health">
                                        <HrMonitorStat label="Open complaints" value={hrComplaints.open ?? counts.grievances ?? 0} tone="danger" />
                                        <HrMonitorStat label="Resolved" value={hrComplaints.resolved ?? 0} tone="good" />
                                        <HrMonitorStat label="High priority" value={hrComplaints.highPriority ?? 0} tone="danger" />
                                        <HrMonitorStat label="Pending over 7 days" value={hrComplaints.pendingBeyond7Days ?? 0} tone="danger" />
                                    </HrMonitorPanel>
                                </section>

                                <section className="premium-card hr-team-health-panel">
                                    <div className="premium-card-header compact">
                                        <div>
                                            <span className="premium-section-kicker">Team-wise HR health</span>
                                            <h2>Headcount, attendance, requests, and payroll readiness</h2>
                                        </div>
                                        <UserRoundCheck size={18} />
                                    </div>
                                    <div className="hr-team-health-grid">
                                        {hrTeams.length ? hrTeams.slice(0, 8).map((team) => (
                                            <div key={team.team} className="hr-team-health-card">
                                                <div>
                                                    <strong>{team.team}</strong>
                                                    <span>{team.headcount || 0} employees</span>
                                                </div>
                                                <div className="hr-team-health-stats">
                                                    <span>Present {team.presentToday || 0}</span>
                                                    <span>Leave {team.leaveCount || 0}</span>
                                                    <span>Late {team.lateCount || 0}</span>
                                                    <span>Pending {team.pendingRequests || 0}</span>
                                                    <span>Payroll {team.payrollReady || 0}/{team.headcount || 0}</span>
                                                </div>
                                            </div>
                                        )) : (
                                            <p className="premium-empty-text">Team health appears after HR data syncs.</p>
                                        )}
                                    </div>
                                </section>

                                <div className="hr-monitor-grid">
                                    <section className="premium-card hr-monitor-main">
                                        <div className="premium-card-header">
                                            <div>
                                                <span className="premium-section-kicker">Live queues</span>
                                                <h2>Employee monitoring</h2>
                                            </div>
                                        </div>

                                        <HrQueueMenu
                                            tabs={hrQueueTabs}
                                            activeKey={selectedHrQueue.key}
                                            onSelect={setActiveHrQueue}
                                            selected={selectedHrQueue}
                                            loading={false}
                                            message={hrQueueMessage}
                                            onNotifyAdmin={notifyAdminForCorrection}
                                        />
                                    </section>

                                    <aside className="hr-monitor-side">
                                        <MissingEodPanel
                                            count={counts.missingEod || 0}
                                            items={queues.missingEod || []}
                                            loading={false}
                                            mode={missingEodMode}
                                            startDate={missingEodStartDate}
                                            endDate={missingEodEndDate}
                                            range={missingEodRange}
                                            onModeChange={(mode) => {
                                                setMissingEodMode(mode);
                                                if (mode === "today") {
                                                    const today = getDateKey();
                                                    setMissingEodStartDate(today);
                                                    setMissingEodEndDate(today);
                                                }
                                            }}
                                            onStartDateChange={setMissingEodStartDate}
                                            onEndDateChange={setMissingEodEndDate}
                                            onRefresh={fetchHrMonitoring}
                                        />

                                        <section className="premium-card hr-side-panel">
                                            <div className="premium-card-header compact">
                                                <div>
                                                    <span className="premium-section-kicker">Employees</span>
                                                    <h2>Team strength</h2>
                                                </div>
                                                <UserRoundCheck size={18} />
                                            </div>
                                            <HrTeamDistribution teams={employeeInsights.byTeam || []} total={counts.activeEmployees || counts.employees || 0} />
                                        </section>

                                        <section
                                            className="premium-card hr-employee-container"
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => setShowHrEmployeeDirectory(true)}
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter" || event.key === " ") setShowHrEmployeeDirectory(true);
                                            }}
                                        >
                                            <div className="premium-card-header compact">
                                                <div>
                                                    <span className="premium-section-kicker">Directory</span>
                                                    <h2>Recent employees</h2>
                                                </div>
                                                <FileText size={18} />
                                            </div>
                                            <div className="hr-employee-list">
                                                {(employeeInsights.recent || []).length ? (
                                                    <HrEmployeePreview employees={employeeInsights.recent || []} />
                                                ) : (
                                                    <p className="premium-empty-text">No employee records found.</p>
                                                )}
                                            </div>
                                        </section>
                                    </aside>
                                </div>
                            </>
                        )}
                    </section>
                </main>
                {showHrEmployeeDirectory && (
                    <HrEmployeeDirectoryModal
                        employees={employeeInsights.recent || []}
                        onClose={() => setShowHrEmployeeDirectory(false)}
                        onSelect={(item) => setSelectedHrEmployee(item)}
                    />
                )}
                {selectedHrEmployee && (
                    <HrEmployeeDetailsModal employee={selectedHrEmployee} onClose={() => setSelectedHrEmployee(null)} />
                )}
            </div>
        );
    }

    if (isManufacturing) {
        return (
            <div className="min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)]">
                <main className="employee-dashboard-premium manufacturing-dashboard">
                    <section className="premium-shell">
                        <div className="premium-hero manufacturing-hero">
                            <div className="premium-hero-copy">
                                <span className="premium-eyebrow">Manufacturing command center</span>
                                <h1>Work order control board</h1>
                                <div className="premium-meta-row">
                                    <span>{employee?.name || "Manufacturing"}</span>
                                    <span>{employee?.team || "Manufacturing"}</span>
                                    <span>{new Date().toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}</span>
                                </div>
                                <div className="manufacturing-hero-actions">
                                    <button type="button" className="manufacturing-primary-action" onClick={() => setIsWorkOrderModalOpen(true)}>
                                        <Plus size={16} />
                                        New work order
                                    </button>
                                    <button type="button" className="manufacturing-primary-action" onClick={() => setIsQuotationModalOpen(true)}>
                                        <Plus size={16} />
                                        Assign quotation
                                    </button>
                                    <button type="button" className="manufacturing-secondary-action" onClick={() => navigate("/quotations")}>
                                        Open list
                                        <ArrowRight size={16} />
                                    </button>
                                </div>
                            </div>

                            <div className="manufacturing-kpi-grid" aria-label="Quotation status summary">
                                <ManufacturingKpi label="Active WOs" value={manufacturingSummary.active || 0} icon={<ClipboardList size={18} />} />
                                <ManufacturingKpi label="Target" value={`${manufacturingSummary.targetRate || 0}%`} icon={<Gauge size={18} />} />
                                <ManufacturingKpi label="OEE" value={`${manufacturingSummary.oee || 0}%`} icon={<TrendingUp size={18} />} />
                            </div>
                        </div>

                        {(quotationMessage || manufacturingMessage) && (
                            <section className="manufacturing-message">
                                {manufacturingMessage || quotationMessage}
                            </section>
                        )}

                        <section className="manufacturing-control-grid">
                            <section className="manufacturing-history-section manufacturing-production-card">
                                <div className="manufacturing-history-header">
                                    <div>
                                        <span className="premium-section-kicker">Phase 1</span>
                                        <h2>Production control</h2>
                                    </div>
                                    <Factory size={18} />
                                </div>
                                <div className="grid gap-3 md:grid-cols-4">
                                    <ManufacturingKpi label="Planned" value={manufacturingSummary.totalPlanned || 0} icon={<Package size={16} />} />
                                    <ManufacturingKpi label="Produced" value={manufacturingSummary.totalProduced || 0} icon={<CheckCircle2 size={16} />} />
                                    <ManufacturingKpi label="Rejected" value={manufacturingSummary.totalRejected || 0} icon={<ShieldAlert size={16} />} />
                                    <ManufacturingKpi label="Downtime" value={`${manufacturingSummary.totalDowntime || 0}m`} icon={<TimerReset size={16} />} />
                                </div>
                            </section>

                            <section className="manufacturing-history-section manufacturing-tracker-card">
                                <div className="manufacturing-history-header">
                                    <div>
                                        <span className="premium-section-kicker">Work orders</span>
                                        <h2>Tracker</h2>
                                    </div>
                                    <ClipboardList size={18} />
                                </div>
                                {manufacturingLoading ? (
                                    <p className="premium-empty-text">Loading work orders...</p>
                                ) : manufacturingWorkOrders.length ? (
                                    <div className="space-y-3">
                                        {manufacturingWorkOrders.slice(0, 8).map((workOrder) => (
                                            <ManufacturingWorkOrderRow
                                                key={workOrder._id}
                                                workOrder={workOrder}
                                                onStatus={(status) => quickUpdateWorkOrder(workOrder, { status })}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="manufacturing-empty-action">
                                        <p>No work orders yet. Create the first work order to unlock machine, material, QC, dispatch, trend, and planning dashboards.</p>
                                        <button type="button" className="manufacturing-primary-action" onClick={() => setIsWorkOrderModalOpen(true)}>
                                            <Plus size={15} />
                                            Create work order
                                        </button>
                                    </div>
                                )}
                            </section>

                            <section className="manufacturing-history-section manufacturing-oee-card">
                                <div className="manufacturing-history-header">
                                    <div>
                                        <span className="premium-section-kicker">OEE</span>
                                        <h2>Efficiency</h2>
                                    </div>
                                    <Gauge size={18} />
                                </div>
                                <div className="grid gap-3">
                                    <MiniMetric label="Availability" value={`${manufacturingSummary.availability || 0}%`} />
                                    <MiniMetric label="Performance" value={`${manufacturingSummary.performance || 0}%`} />
                                    <MiniMetric label="Quality" value={`${manufacturingSummary.qualityRate || 0}%`} />
                                </div>
                            </section>

                            <section className="manufacturing-history-section manufacturing-risk-card">
                                <div className="manufacturing-history-header">
                                    <div>
                                        <span className="premium-section-kicker">Alerts</span>
                                        <h2>Risks</h2>
                                    </div>
                                    <AlertCircle size={18} />
                                </div>
                                <div className="space-y-2">
                                    <ManufacturingAlert label="Delayed jobs" value={manufacturingSummary.delayed || 0} />
                                    <ManufacturingAlert label="Material shortages" value={manufacturingSummary.materialShortage || 0} />
                                    <ManufacturingAlert label="Machine down" value={manufacturingSummary.machineDown || 0} />
                                    <ManufacturingAlert label="QC pending" value={manufacturingSummary.qcPending || 0} />
                                </div>
                            </section>
                        </section>

                        {manufacturingWorkOrders.length > 0 && (
                            <>
                                <section className="grid gap-4 lg:grid-cols-3">
                                    <ManufacturingInsightCard title="Machine status" icon={<Wrench size={18} />} items={manufacturingWorkOrders.map((item) => `${item.machine?.name || item.assignedLine || item.workOrderNo}: ${formatWorkOrderStatus(item.machine?.status)}`).slice(0, 5)} />
                                    <ManufacturingInsightCard title="Material readiness" icon={<Package size={18} />} items={manufacturingWorkOrders.map((item) => `${item.workOrderNo}: ${formatWorkOrderStatus(item.material?.status)}`).slice(0, 5)} />
                                    <ManufacturingInsightCard title="QC / dispatch" icon={<Truck size={18} />} items={manufacturingWorkOrders.map((item) => `${item.workOrderNo}: QC ${formatWorkOrderStatus(item.quality?.status)} / ${formatWorkOrderStatus(item.dispatch?.status)}`).slice(0, 5)} />
                                </section>

                                <section className="manufacturing-planning-grid">
                                    <section className="manufacturing-history-section">
                                        <div className="manufacturing-history-header">
                                            <div>
                                                <span className="premium-section-kicker">Phase 3</span>
                                                <h2>Trend charts</h2>
                                            </div>
                                            <TrendingUp size={18} />
                                        </div>
                                        <div className="space-y-3">
                                            {manufacturingTrends.length ? manufacturingTrends.map((item) => (
                                                <ManufacturingTrend key={item.label} item={item} />
                                            )) : <p className="premium-empty-text">Trends appear after work orders are updated.</p>}
                                        </div>
                                    </section>

                                    <section className="manufacturing-history-section">
                                        <div className="manufacturing-history-header">
                                            <div>
                                                <span className="premium-section-kicker">Planning</span>
                                                <h2>Capacity forecast</h2>
                                            </div>
                                            <CalendarDays size={18} />
                                        </div>
                                        <div className="grid gap-3 md:grid-cols-2">
                                            <MiniMetric label="Open capacity" value={`${Math.max(0, 100 - (manufacturingSummary.targetRate || 0))}%`} />
                                            <MiniMetric label="Dispatch ready" value={manufacturingSummary.dispatchReady || 0} />
                                            <MiniMetric label="Pending approvals" value={manufacturingWorkOrders.filter((item) => item.supervisorApproval?.status === "pending").length} />
                                            <MiniMetric label="Maintenance open" value={manufacturingWorkOrders.filter((item) => item.maintenanceRequest?.status && item.maintenanceRequest.status !== "none" && item.maintenanceRequest.status !== "resolved").length} />
                                        </div>
                                    </section>
                                </section>
                            </>
                        )}

                        <section className="manufacturing-history-section">
                            <div className="manufacturing-history-header">
                                <div>
                                    <span className="premium-section-kicker">History</span>
                                    <h2>Quotation history</h2>
                                </div>
                                <IndianRupee size={18} />
                            </div>
                            <div className="manufacturing-recent-list">
                                {quotationLoading ? (
                                    <p className="premium-empty-text">Loading quotations...</p>
                                ) : quotations.length ? quotations.slice(0, 6).map((quotation) => (
                                    <QuotationSummaryRow key={quotation._id} quotation={quotation} onOpen={() => setSelectedQuotation(quotation)} />
                                )) : (
                                    <p className="premium-empty-text">No quotations assigned yet.</p>
                                )}
                            </div>
                        </section>
                    </section>
                </main>
                {isWorkOrderModalOpen && (
                    <div className="manufacturing-modal-backdrop" role="dialog" aria-modal="true" aria-label="Create work order">
                        <section className="manufacturing-form-card manufacturing-modal manufacturing-work-order-modal">
                            <div className="manufacturing-card-header">
                                <div>
                                    <span className="premium-section-kicker">New work order</span>
                                    <h2>Production setup</h2>
                                </div>
                                <button type="button" className="manufacturing-icon-button" onClick={() => setIsWorkOrderModalOpen(false)} aria-label="Close work order form">
                                    <X size={18} />
                                </button>
                            </div>

                            <form className="manufacturing-form" onSubmit={submitWorkOrder}>
                                <div className="manufacturing-work-order-modal-body">
                                    <div className="manufacturing-modal-section">
                                        <span className="manufacturing-modal-section-title">Order details</span>
                                        <div className="manufacturing-field-grid manufacturing-work-order-grid">
                                            <QuotationInput label="Work order no" value={workOrderForm.workOrderNo} onChange={(value) => setWorkOrderField("workOrderNo", value)} />
                                            <QuotationInput label="Customer" value={workOrderForm.customerName} onChange={(value) => setWorkOrderField("customerName", value)} />
                                            <QuotationInput label="Product" value={workOrderForm.product} onChange={(value) => setWorkOrderField("product", value)} required />
                                            <QuotationInput label="Due date" type="date" value={workOrderForm.dueDate} onChange={(value) => setWorkOrderField("dueDate", value)} />
                                            <QuotationInput label="Planned qty" type="number" value={workOrderForm.plannedQty} onChange={(value) => setWorkOrderField("plannedQty", value)} />
                                            <QuotationInput label="Produced qty" type="number" value={workOrderForm.producedQty} onChange={(value) => setWorkOrderField("producedQty", value)} />
                                            <QuotationInput label="Assigned line" value={workOrderForm.assignedLine} onChange={(value) => setWorkOrderField("assignedLine", value)} />
                                            <QuotationInput label="Owner" value={workOrderForm.assignedOwner} onChange={(value) => setWorkOrderField("assignedOwner", value)} />
                                        </div>
                                    </div>

                                    <div className="manufacturing-modal-section">
                                        <span className="manufacturing-modal-section-title">Control status</span>
                                        <div className="manufacturing-work-order-select-grid">
                                            <ManufacturingSelect label="Priority" value={workOrderForm.priority} options={["low", "normal", "high", "urgent"]} onChange={(value) => setWorkOrderField("priority", value)} />
                                            <ManufacturingSelect label="Status" value={workOrderForm.status} options={["planned", "in_progress", "hold", "qc_pending", "dispatch_ready", "dispatched", "completed"]} onChange={(value) => setWorkOrderField("status", value)} />
                                            <ManufacturingSelect label="Material" value={workOrderForm.material.status} options={["ready", "shortage", "purchase_required", "issued"]} onChange={(value) => setWorkOrderNestedField("material", "status", value)} />
                                            <ManufacturingSelect label="Quality" value={workOrderForm.quality.status} options={["not_started", "pending", "passed", "rework", "rejected"]} onChange={(value) => setWorkOrderNestedField("quality", "status", value)} />
                                            <ManufacturingSelect label="Dispatch" value={workOrderForm.dispatch.status} options={["not_ready", "packing", "ready", "dispatched"]} onChange={(value) => setWorkOrderNestedField("dispatch", "status", value)} />
                                            <ManufacturingSelect label="Machine status" value={workOrderForm.machine.status} options={["running", "idle", "breakdown", "maintenance", "setup"]} onChange={(value) => setWorkOrderNestedField("machine", "status", value)} />
                                        </div>
                                    </div>

                                    <div className="manufacturing-modal-section manufacturing-machine-section">
                                        <span className="manufacturing-modal-section-title">Machine and handover</span>
                                        <div className="manufacturing-field-grid manufacturing-work-order-grid">
                                            <QuotationInput label="Machine" value={workOrderForm.machine.name} onChange={(value) => setWorkOrderNestedField("machine", "name", value)} />
                                            <QuotationInput label="Downtime minutes" type="number" value={workOrderForm.machine.downtimeMinutes} onChange={(value) => setWorkOrderNestedField("machine", "downtimeMinutes", value)} />
                                        </div>

                                        <label className="manufacturing-textarea">
                                            <span>Delay / handover notes</span>
                                            <textarea
                                                value={workOrderForm.handoverNotes}
                                                onChange={(event) => setWorkOrderField("handoverNotes", event.target.value)}
                                                rows={4}
                                                placeholder="Shift handover, material issue, QC hold, machine condition, dispatch priority..."
                                            />
                                        </label>
                                    </div>
                                </div>

                                <div className="manufacturing-actions manufacturing-modal-footer">
                                    <button type="button" onClick={() => setIsWorkOrderModalOpen(false)} className="manufacturing-secondary-action">
                                        Cancel
                                    </button>
                                    <button type="submit" disabled={workOrderSaving} className="manufacturing-primary-action">
                                        <Plus size={16} />
                                        {workOrderSaving ? "Creating..." : "Create work order"}
                                    </button>
                                </div>
                            </form>
                        </section>
                    </div>
                )}
                {isQuotationModalOpen && (
                    <div className="manufacturing-modal-backdrop" role="dialog" aria-modal="true" aria-label="Assign new quotation">
                        <section className="manufacturing-form-card manufacturing-modal">
                            <div className="manufacturing-card-header">
                                <div>
                                    <span className="premium-section-kicker">New quotation</span>
                                    <h2>Assign to revenue teams</h2>
                                </div>
                                <button type="button" className="manufacturing-icon-button" onClick={() => setIsQuotationModalOpen(false)} aria-label="Close quotation form">
                                    <X size={18} />
                                </button>
                            </div>

                            <form className="manufacturing-form" onSubmit={submitQuotation}>
                                <div className="manufacturing-field-grid">
                                    <QuotationInput label="Quotation title" value={quotationForm.title} onChange={(value) => setQuotationField("title", value)} required />
                                    <QuotationInput label="Customer name" value={quotationForm.customerName} onChange={(value) => setQuotationField("customerName", value)} />
                                    <QuotationInput label="Quotation number" value={quotationForm.quotationNo} onChange={(value) => setQuotationField("quotationNo", value)} />
                                    <QuotationInput label="Amount" type="number" value={quotationForm.amount} onChange={(value) => setQuotationField("amount", value)} />
                                    <QuotationInput label="Due date" type="date" value={quotationForm.dueDate} onChange={(value) => setQuotationField("dueDate", value)} />
                                    <div className="manufacturing-target-card">
                                        <p>Visible to</p>
                                        <div>
                                            {quotationTargets.map((team) => (
                                                <button
                                                    key={team.value}
                                                    type="button"
                                                    onClick={() => toggleQuotationTarget(team.value)}
                                                    className={`manufacturing-target-button ${quotationForm.targetTeams.includes(team.value) ? "active" : ""}`}
                                                >
                                                    {team.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <label className="manufacturing-textarea">
                                    <span>Quotation details</span>
                                    <textarea
                                        value={quotationForm.details}
                                        onChange={(event) => setQuotationField("details", event.target.value)}
                                        rows={5}
                                        placeholder="Items, quantity, pricing notes, requirement, and follow-up..."
                                    />
                                </label>

                                <label className="manufacturing-photo-upload">
                                    <span>Quotation photo</span>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(event) => setQuotationPhoto(event.target.files?.[0])}
                                        required={!quotationForm.proofPhoto?.data}
                                        className="quotation-photo-input"
                                    />
                                    <div className="quotation-photo-body">
                                        {quotationForm.proofPhoto?.data ? (
                                            <img src={quotationForm.proofPhoto.data} alt="Quotation preview" />
                                        ) : (
                                            <span className="quotation-photo-placeholder">
                                                <ImagePlus size={18} />
                                            </span>
                                        )}
                                        <div>
                                            <strong>{quotationForm.proofPhoto?.name || "Upload quotation image"}</strong>
                                            <small>Photo required before assigning</small>
                                        </div>
                                        <em>
                                            <ImagePlus size={14} />
                                            {quotationForm.proofPhoto?.data ? "Change photo" : "Choose photo"}
                                        </em>
                                    </div>
                                </label>

                                <div className="manufacturing-actions">
                                    <button type="button" onClick={() => setIsQuotationModalOpen(false)} className="manufacturing-secondary-action">
                                        Cancel
                                    </button>
                                    <button type="submit" disabled={quotationSaving} className="manufacturing-primary-action">
                                        <Plus size={16} />
                                        {quotationSaving ? "Assigning..." : "Assign quotation"}
                                    </button>
                                </div>
                            </form>
                        </section>
                    </div>
                )}
                {selectedQuotation && (
                    <QuotationDetailsModal quotation={selectedQuotation} onClose={() => setSelectedQuotation(null)} />
                )}
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)]">
            <main className="employee-dashboard-premium">
                <section className="premium-shell">
                    <div className="premium-hero">
                        <div className="premium-hero-copy">
                            <span className="premium-eyebrow">{workspaceLabel}</span>
                            <h1>{greeting()}, {employee?.name || "there"}</h1>
                            <div className="premium-meta-row">
                                <span>{employee?.team || "Team"}</span>
                                <span>{formatRoleLabel(employee?.role) || "Employee"}</span>
                                <span>{new Date().toLocaleDateString("en-IN", {
                                    weekday: "short",
                                    day: "numeric",
                                    month: "short",
                                })}</span>
                            </div>
                        </div>

                        <div className="premium-hero-stats" aria-label="Dashboard summary">
                            <MiniMetric label="Pending" value={taskStats.pending} />
                            <MiniMetric label="Assets" value={assetCount} />
                            <MiniMetric label="Unread" value={unreadCount} />
                        </div>
                    </div>

                    <div className="premium-quick-grid">
                        <QuickActionButton
                            icon={<Clock3 size={18} />}
                            label="Check Attendance"
                            badge={attendanceView.label}
                            onClick={() => navigate("/attendance")}
                        />
                        <QuickActionButton
                            icon={<CalendarDays size={18} />}
                            label="Apply Leave"
                            onClick={() => navigate("/leave", { state: { openApplyLeave: true } })}
                        />
                        <QuickActionButton
                            icon={<FileText size={18} />}
                            label={todayEOD?.status === "submitted" ? "View EOD" : todayEOD?.status === "draft" ? "Continue EOD" : "Submit EOD"}
                            onClick={() => navigate("/eod")}
                        />
                        <QuickActionButton
                            icon={<ClipboardList size={18} />}
                            label="View Tasks"
                            badge={`${taskStats.pending} pending`}
                            onClick={() => navigate("/tasks")}
                        />
                        <QuickActionButton
                            icon={<ReceiptText size={18} />}
                            label="Claim Expense"
                            onClick={() => navigate("/expenses")}
                        />
                        <QuickActionButton
                            icon={<WalletCards size={18} />}
                            label="Salary Slip"
                            onClick={() => setShowSalarySlip(true)}
                        />
                    </div>

                    {loadError && (
                        <section className="premium-card border border-red-500/20 bg-red-500/10 text-red-500">
                            <div className="flex items-center gap-2 text-sm font-bold">
                                <AlertCircle size={18} />
                                <span>{loadError}</span>
                            </div>
                        </section>
                    )}

                    <TodayChecklist
                        attendanceStatus={attendanceStatus}
                        record={record}
                        todayEOD={todayEOD}
                        pendingTasks={taskStats.pending}
                        unreadAnnouncements={unreadCount}
                        onAttendance={() => navigate("/attendance")}
                        onEod={() => navigate("/eod")}
                        onTasks={() => navigate("/tasks")}
                        onAnnouncements={() => navigate("/announcements")}
                    />

                    <div className="premium-grid">
                        <section className="premium-card premium-attendance-card">
                            <div className="premium-card-header">
                                <div>
                                    <span className="premium-section-kicker">Today</span>
                                    <h2>Attendance</h2>
                                </div>
                                <span className={`premium-status-pill ${attendanceView.tone}`}>
                                    {attendanceView.label}
                                </span>
                            </div>

                            <div className="premium-attendance-body">
                                <div className={`premium-attendance-icon ${attendanceView.tone}`}>
                                    {attendanceView.icon}
                                </div>
                                <div>
                                    <p className="premium-attendance-title">{attendanceView.title}</p>
                                    <p className="premium-attendance-subtitle">{attendanceView.subtitle}</p>
                                </div>
                            </div>

                            <div className="premium-attendance-timeline">
                                <TimelineItem label="Check in" value={formatTime(record?.checkIn)} />
                                <TimelineItem label="Mode" value={record?.mode || "-"} />
                                <TimelineItem label="Check out" value={formatCheckout(record)} />
                                <TimelineItem label="Work" value={workDuration} />
                            </div>

                            <div className="premium-actions">
                                <button
                                    className="premium-secondary-button"
                                    type="button"
                                    onClick={() => navigate("/attendance")}
                                >
                                    Open Attendance
                                    <ArrowRight size={16} />
                                </button>
                            </div>
                        </section>

                        <aside className="premium-side-stack">
                            <section className="premium-card">
                                <div className="premium-card-header compact">
                                    <div>
                                        <span className="premium-section-kicker">Daily close</span>
                                        <h2>Today EOD</h2>
                                    </div>
                                    <ClipboardList size={18} />
                                </div>

                                <div className="premium-eod-state">
                                    <span className={`premium-eod-dot ${todayEOD?.status || "draft"}`} />
                                    <div>
                                        <p>{getEodTitle(todayEOD)}</p>
                                        <span>{getEodSubtitle(todayEOD)}</span>
                                    </div>
                                </div>

                                {todayEOD?.status === "submitted" ? (
                                    <button
                                        className="premium-submitted-button"
                                        type="button"
                                        onClick={() => navigate("/eod")}
                                    >
                                        <CheckCircle2 size={16} />
                                        Submitted
                                    </button>
                                ) : (
                                    <button
                                        className="premium-secondary-button"
                                        type="button"
                                        onClick={() => navigate("/eod")}
                                    >
                                        {todayEOD?.status === "draft" ? "Continue EOD" : "Start EOD"}
                                    </button>
                                )}
                            </section>

                            <section className="premium-card">
                                <div className="premium-card-header compact">
                                    <div>
                                        <span className="premium-section-kicker">Assigned</span>
                                        <h2>Assets</h2>
                                    </div>
                                    <Package size={18} />
                                </div>

                                <div className="premium-asset-list">
                                    {assetCount === 0 ? (
                                        <p className="premium-empty-text">No assets assigned</p>
                                    ) : (
                                        employee.assignedAssets.slice(0, 3).map((asset, index) => (
                                            <AssetRow
                                                key={asset._id || `${asset.assetName}-${index}`}
                                                asset={asset}
                                            />
                                        ))
                                    )}
                                </div>
                            </section>
                        </aside>

                        <section className="premium-stat-row">
                            <StatCard
                                label="Pending"
                                value={taskStats.pending}
                                icon={<ClipboardList />}
                                tone="amber"
                                onClick={() => navigate("/tasks")}
                            />
                            <StatCard
                                label="In Progress"
                                value={taskStats.inProgress}
                                icon={<Loader2 />}
                                tone="blue"
                                onClick={() => navigate("/tasks")}
                            />
                            <StatCard
                                label="Completed"
                                value={taskStats.completed}
                                icon={<CheckCircle2 />}
                                tone="green"
                                onClick={() => navigate("/tasks")}
                            />
                        </section>

                        <section className="premium-card">
                            <div className="premium-card-header">
                                <div>
                                    <span className="premium-section-kicker">Recognition</span>
                                    <h2>Badges</h2>
                                </div>
                                <Award size={18} />
                            </div>

                            {recognitionBadges.length === 0 ? (
                                <p className="premium-empty-text">No badges yet. Keep building your streak.</p>
                            ) : (
                                <div className={`mt-4 grid gap-3 ${recognitionBadges.length > 1 ? "md:grid-cols-2" : ""}`}>
                                    {recognitionBadges.map((badge) => (
                                        <RecognitionBadge
                                            key={badge.key}
                                            badge={badge}
                                            onClick={() => setSelectedBadge(badge)}
                                        />
                                    ))}
                                </div>
                            )}
                        </section>

                        {getTeamMetrics(employee?.team, todayEOD?.teamMetrics).length > 0 && (
                            <section className="premium-card premium-team-card">
                                <div className="premium-card-header">
                                    <div>
                                        <span className="premium-section-kicker">Team performance</span>
                                        <h2>{employee.team} snapshot</h2>
                                    </div>
                                    <TimerReset size={18} />
                                </div>

                                <div className="premium-team-grid">
                                    {getTeamMetrics(employee.team, todayEOD?.teamMetrics).map((metric) => (
                                        <MiniMetric key={metric.label} label={metric.label} value={metric.value} />
                                    ))}
                                </div>
                            </section>
                        )}

                        <section className="premium-card premium-announcements-card">
                            <div className="premium-card-header">
                                <div>
                                    <span className="premium-section-kicker">Updates</span>
                                    <h2>Announcements</h2>
                                </div>
                                <span className="premium-unread-count">{unreadCount} unread</span>
                            </div>

                            <div className="premium-announcement-grid">
                                {latestAnnouncements.length === 0 ? (
                                    <p className="premium-empty-text">No announcements</p>
                                ) : (
                                    latestAnnouncements.map((announcement) => (
                                        <AnnouncementCard
                                            key={announcement._id}
                                            announcement={announcement}
                                        />
                                    ))
                                )}
                            </div>

                            <button
                                className="premium-link-button"
                                onClick={() => navigate("/announcements")}
                            >
                                View all announcements
                                <ArrowRight size={15} />
                            </button>
                        </section>
                    </div>
                </section>
            </main>
            <SalarySlipModal open={showSalarySlip} onClose={() => setShowSalarySlip(false)} />
            <BadgeDetailsModal badge={selectedBadge} onClose={() => setSelectedBadge(null)} />
        </div>
    );
};

const getAttendanceView = (attendanceStatus, record, formatTime, formatCheckout) => {
    if (attendanceStatus?.status === "checked_out") {
        return {
            label: "Completed",
            title: "Day completed",
            subtitle: (
                <span className="inline-flex flex-wrap items-center gap-1">
                    <span>{formatTime(record?.checkIn)}</span>
                    <span>-</span>
                    {formatCheckout(record)}
                </span>
            ),
            tone: "success",
            icon: <CheckCircle2 size={24} />,
        };
    }

    if (attendanceStatus?.status === "checked_in") {
        return {
            label: "Live",
            title: "Checked in",
            subtitle: `Started at ${formatTime(record?.checkIn)}`,
            tone: "success",
            icon: <Clock3 size={24} />,
        };
    }

    if (attendanceStatus?.status === "on_break") {
        return {
            label: "On break",
            title: "Break running",
            subtitle: `Started at ${formatTime(record?.breakStart)}`,
            tone: "warning",
            icon: <TimerReset size={24} />,
        };
    }

    return {
        label: "Not started",
        title: "Not checked in",
        subtitle: "Start attendance when your shift begins",
        tone: "warning",
        icon: <AlertCircle size={24} />,
    };
};

const getEodTitle = (todayEOD) => {
    if (todayEOD?.status === "submitted") return "Submitted";
    if (todayEOD?.status === "draft") return "Draft saved";
    return "Not started";
};

const getEodSubtitle = (todayEOD) => {
    if (todayEOD?.status === "submitted") return "Your report is complete";
    if (todayEOD?.status === "draft") return "Finish before the day closes";
    return "Create your daily report";
};

const getTeamMetrics = (team = "", metrics = {}) => {
    const teamName = team.toLowerCase();

    if (teamName.includes("sales")) {
        return [
            { label: "Deals", value: metrics["Deals Closed"] || "0" },
            { label: "Revenue", value: metrics.Revenue || "0" },
            { label: "Meetings", value: metrics["Client Meetings"] || "0" },
            { label: "Demos", value: metrics["Product Demos"] || "0" },
        ];
    }

    if (teamName.includes("tele")) {
        return [
            { label: "Calls", value: metrics["Total Calls"] || "0" },
            { label: "Connected", value: metrics["Connected Calls"] || "0" },
            { label: "Leads", value: metrics["Interested Leads"] || "0" },
            { label: "Follow-ups", value: metrics["Follow-ups Scheduled"] || "0" },
        ];
    }

    if (teamName.includes("digital")) {
        return [
            { label: "Campaigns", value: metrics["Active Campaigns"] || "0" },
            { label: "Leads", value: metrics["Leads Generated"] || "0" },
            { label: "Visits", value: metrics["Website Visits"] || "0" },
            { label: "Reach", value: metrics["Social Media Reach"] || "0" },
        ];
    }

    if (teamName.includes("manufactur")) {
        return [
            { label: "Planned", value: metrics["Planned Production"] || "0" },
            { label: "Actual", value: metrics["Actual Production"] || "0" },
            { label: "Rejected", value: metrics["Rejected Units"] || "0" },
            { label: "Downtime", value: metrics.Downtime || "0" },
        ];
    }

    return [];
};

const MiniMetric = ({ label, value, className = "" }) => (
    <div className={`premium-mini-metric ${className}`}>
        <span>{label}</span>
        <strong>{value}</strong>
    </div>
);

const formatWorkOrderStatus = (value = "") => String(value || "-").replace(/_/g, " ");

const ManufacturingWorkOrderRow = ({ workOrder, onStatus }) => (
    <article className="manufacturing-work-order-row">
        <div>
            <span>{workOrder.priority || "normal"}</span>
            <p>{workOrder.workOrderNo} - {workOrder.product}</p>
            <small>{workOrder.customerName || "No customer"} | {workOrder.assignedLine || "Line not assigned"}</small>
        </div>
        <div>
            <MiniMetric label="Plan" value={workOrder.plannedQty || 0} />
            <MiniMetric label="Made" value={workOrder.producedQty || 0} />
            <MiniMetric label="QC" value={formatWorkOrderStatus(workOrder.quality?.status)} />
        </div>
        <div>
            <select
                value={workOrder.status || "planned"}
                onChange={(event) => onStatus(event.target.value)}
                className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 py-2 text-xs font-black capitalize outline-none"
            >
                {["planned", "in_progress", "hold", "qc_pending", "dispatch_ready", "dispatched", "completed"].map((status) => (
                    <option key={status} value={status}>{formatWorkOrderStatus(status)}</option>
                ))}
            </select>
        </div>
    </article>
);

const ManufacturingAlert = ({ label, value }) => (
    <div className="flex items-center justify-between rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 py-2">
        <span className="text-xs font-black uppercase text-[var(--text-secondary)]">{label}</span>
        <strong className={Number(value) ? "text-rose-500" : "text-emerald-500"}>{value}</strong>
    </div>
);

const ManufacturingInsightCard = ({ title, icon, items }) => (
    <section className="manufacturing-history-section">
        <div className="manufacturing-history-header">
            <div>
                <span className="premium-section-kicker">Live board</span>
                <h2>{title}</h2>
            </div>
            {icon}
        </div>
        <div className="space-y-2">
            {items.length ? items.map((item, index) => (
                <p key={`${item}-${index}`} className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 py-2 text-sm font-bold capitalize text-[var(--text-primary)]">
                    {item}
                </p>
            )) : <p className="premium-empty-text">No records yet.</p>}
        </div>
    </section>
);

const ManufacturingTrend = ({ item }) => {
    const total = Math.max(1, Number(item.produced || 0) + Number(item.rejected || 0) + Number(item.downtime || 0));
    return (
        <div>
            <div className="mb-1 flex items-center justify-between text-xs font-black">
                <span>{item.label}</span>
                <span>{item.produced || 0} produced</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-[var(--bg-secondary)]">
                <div className="h-full bg-[var(--accent-primary)]" style={{ width: `${Math.min(100, ((item.produced || 0) / total) * 100)}%` }} />
            </div>
        </div>
    );
};

const ManufacturingSelect = ({ label, value, options, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div
            className={`manufacturing-input manufacturing-select ${isOpen ? "open" : ""}`}
            tabIndex={-1}
            onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) setIsOpen(false);
            }}
        >
            <span>{label}</span>
            <button
                type="button"
                className="manufacturing-select-trigger"
                onClick={() => setIsOpen((current) => !current)}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
            >
                <strong>{formatWorkOrderStatus(value)}</strong>
                <ChevronDown size={15} />
            </button>
            {isOpen && (
                <div className="manufacturing-select-menu" role="listbox">
                    {options.map((option) => (
                        <button
                            key={option}
                            type="button"
                            role="option"
                            aria-selected={option === value}
                            className={option === value ? "active" : ""}
                            onClick={() => {
                                onChange(option);
                                setIsOpen(false);
                            }}
                        >
                            {formatWorkOrderStatus(option)}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const ManufacturingKpi = ({ label, value, icon }) => (
    <div className="manufacturing-kpi">
        {icon && <span>{icon}</span>}
        <small>{label}</small>
        <strong>{value}</strong>
    </div>
);

const QuotationInput = ({ label, value, onChange, type = "text", required = false }) => (
    <label className="manufacturing-input">
        <span>{label}</span>
        <input
            type={type}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            required={required}
            className="mt-2 w-full bg-transparent text-sm font-bold outline-none"
        />
    </label>
);

const formatQuotationDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const QuotationSummaryRow = ({ quotation, onOpen }) => (
    <button type="button" className="manufacturing-quotation-row" onClick={onOpen}>
        <div className="manufacturing-quotation-photo">
            {quotation.proofPhoto?.data ? (
                <img src={quotation.proofPhoto.data} alt={quotation.proofPhoto.name || "Quotation"} />
            ) : (
                <span>
                    <ImagePlus size={18} />
                </span>
            )}
        </div>
        <div>
            <div>
                <p>{quotation.title}</p>
                <small>
                    {quotation.customerName || "No customer"} {quotation.quotationNo ? `- ${quotation.quotationNo}` : ""}
                </small>
            </div>
            <span>
                {formatQuotationStatus(quotation.status)}
            </span>
        </div>
        <div>
            <span>{formatMoney(quotation.amount)}</span>
            <span>{(quotation.targetTeams || []).length} team{(quotation.targetTeams || []).length === 1 ? "" : "s"}</span>
        </div>
    </button>
);

const QuotationDetailsModal = ({ quotation, onClose }) => (
    <div className="manufacturing-modal-backdrop" role="dialog" aria-modal="true" aria-label="Quotation details">
        <section className="quotation-detail-modal">
            <div className="quotation-detail-head">
                <div>
                    <span className="premium-section-kicker">Quotation details</span>
                    <h2>{quotation.title || "Quotation"}</h2>
                    <p>{quotation.customerName || "No customer name"} {quotation.quotationNo ? `- ${quotation.quotationNo}` : ""}</p>
                </div>
                <span className="quotation-detail-status">{formatQuotationStatus(quotation.status)}</span>
                <button type="button" className="manufacturing-icon-button" onClick={onClose} aria-label="Close quotation details">
                    <X size={18} />
                </button>
            </div>

            <div className="quotation-detail-grid">
                <div className="quotation-detail-photo">
                    {quotation.proofPhoto?.data ? (
                        <img src={quotation.proofPhoto.data} alt={quotation.proofPhoto.name || "Quotation proof"} />
                    ) : (
                        <span>
                            <ImagePlus size={26} />
                            No photo uploaded
                        </span>
                    )}
                </div>

                <div className="quotation-detail-info">
                    <div className="quotation-detail-metrics">
                        <MiniMetric label="Amount" value={formatMoney(quotation.amount)} />
                        <MiniMetric label="Due Date" value={formatQuotationDate(quotation.dueDate)} />
                        <MiniMetric className="quotation-visible-to" label="Visible To" value={(quotation.targetTeams || []).map(formatTeamLabel).join(", ") || "-"} />
                        <MiniMetric label="Quotation No" value={quotation.quotationNo || "-"} />
                    </div>

                    <div className="quotation-detail-notes">
                        <span>Details</span>
                        <p>{quotation.details || "No quotation details added."}</p>
                    </div>

                    <div className="quotation-detail-footer">
                        <span>Assigned by {quotation.assignedByName || "Manufacturing"}</span>
                        <span>{formatQuotationDate(quotation.createdAt || quotation.updatedAt)}</span>
                    </div>
                </div>
            </div>
        </section>
    </div>
);

const HrMetric = ({ label, value, icon }) => (
    <div className="hr-monitor-kpi">
        <span>{icon}</span>
        <small>{label}</small>
        <strong>{value}</strong>
    </div>
);

const HrActionButton = ({ icon, label, onClick }) => (
    <button type="button" className="hr-admin-action" onClick={onClick}>
        <span>{icon}</span>
        <strong>{label}</strong>
        <ArrowRight size={15} />
    </button>
);

const HrMonitorPanel = ({ kicker, title, children }) => (
    <section className="premium-card hr-monitor-insight-panel">
        <div className="premium-card-header compact">
            <div>
                <span className="premium-section-kicker">{kicker}</span>
                <h2>{title}</h2>
            </div>
        </div>
        <div className="hr-monitor-stat-list">{children}</div>
    </section>
);

const HrMonitorStat = ({ label, value, tone = "neutral" }) => (
    <div className={`hr-monitor-stat ${tone}`}>
        <span>{label}</span>
        <strong>{value}</strong>
    </div>
);

const HrQueueMenu = ({ tabs = [], activeKey, onSelect, selected, loading = false, message = "", onNotifyAdmin }) => {
    const [activeDateGroup, setActiveDateGroup] = useState(null);
    const [modalStatusFilter, setModalStatusFilter] = useState("all");
    const [expandedMonths, setExpandedMonths] = useState([new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })]);
    const [seenQueueCounts, setSeenQueueCounts] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem("hr_queue_seen_counts_v1") || "{}");
        } catch {
            return {};
        }
    });
    const groupedItems = groupQueueItemsByDate((selected?.items || []).slice(0, 12));
    const isCorrectionQueue = selected?.key === "attendanceCorrections";
    const visibleModalItems = (activeDateGroup?.items || []).filter((item) => {
        const status = String(item.status || "").toLowerCase();
        if (modalStatusFilter === "approved") return isApprovedQueueStatus(status);
        if (modalStatusFilter === "pending") return ["pending", "pending_lead", "open", "reviewing"].includes(status);
        return true;
    });

    const itemsByMonth = useMemo(() => {
        return groupedItems.reduce((acc, group) => {
            const d = new Date(group.date);
            const monthKey = d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
            if (!acc[monthKey]) acc[monthKey] = [];
            acc[monthKey].push(group);
            return acc;
        }, {});
    }, [groupedItems]);

    return (
        <div className="hr-queue-menu-panel">
            <nav className="hr-queue-menu-tabs" aria-label="HR queue categories">
                {tabs.map((tab) => {
                    const tabCount = Number(tab.count || 0);
                    const hasNewItems = tabCount > Number(seenQueueCounts[tab.key] || 0);
                    return (
                        <button
                            type="button"
                            key={tab.key}
                            className={activeKey === tab.key ? "active" : ""}
                            onClick={() => {
                                setActiveDateGroup(null);
                                setExpandedMonths([new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })]);
                                setSeenQueueCounts((current) => {
                                    const next = { ...current, [tab.key]: tabCount };
                                    localStorage.setItem("hr_queue_seen_counts_v1", JSON.stringify(next));
                                    return next;
                                });
                                onSelect(tab.key);
                            }}
                        >
                            <span className="hr-queue-tab-label">
                                {tab.label}
                            </span>
                            <span className={`hr-queue-tab-count ${hasNewItems ? "has-new" : ""}`}>
                                <strong>{loading ? "..." : tab.count}</strong>
                            </span>
                        </button>
                    );
                })}
            </nav>

            <section className="hr-queue-menu-content">
                <div className="hr-queue-menu-title">
                    <div>
                        <span>Selected queue</span>
                        <h3>{selected?.label || "Queue"}</h3>
                    </div>
                    <strong>{loading ? "..." : selected?.count || 0}</strong>
                </div>
                <div className="hr-monitor-queue-items">
                    {loading ? (
                        <p>Loading HR queue data...</p>
                    ) : Object.keys(itemsByMonth).length ? Object.entries(itemsByMonth).map(([monthKey, groups]) => {
                        const isExpanded = expandedMonths.includes(monthKey);
                        return (
                            <div key={monthKey} className="hr-queue-month-group">
                                <button
                                    type="button"
                                    className="hr-queue-month-header"
                                    onClick={() => setExpandedMonths((prev) => prev.includes(monthKey) ? prev.filter((m) => m !== monthKey) : [...prev, monthKey])}
                                >
                                    <span>{monthKey}</span>
                                    <ChevronDown size={16} className={`transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                                </button>
                                {isExpanded && (
                                    <div className="hr-queue-month-items">
                                        {groups.map((group) => (
                                            <button
                                                type="button"
                                                className="hr-queue-date-wrapper"
                                                key={group.date}
                                                onClick={() => {
                                                    setModalStatusFilter("all");
                                                    setActiveDateGroup(group);
                                                }}
                                            >
                                                <div>
                                                    <span>{formatShortDate(group.date)}</span>
                                                    <strong>{group.items.length} item{group.items.length === 1 ? "" : "s"}</strong>
                                                </div>
                                                <small>Touch to view details</small>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    }) : (
                        <p>{hrQueueEmptyMessages[selected?.key] || "No open items"}</p>
                    )}
                </div>
                {message && <div className="hr-queue-message">{message}</div>}
            </section>

            {activeDateGroup && (
                <div className="hr-queue-modal" role="dialog" aria-modal="true">
                    <button type="button" className="hr-queue-modal-backdrop" onClick={() => setActiveDateGroup(null)} aria-label="Close details" />
                    <div className="hr-queue-modal-card">
                        <div className="hr-queue-modal-head">
                            <div>
                                <span>{selected?.label || "Queue"}</span>
                                <h3>{formatShortDate(activeDateGroup.date)}</h3>
                                <p>{visibleModalItems.length} of {activeDateGroup.items.length} item{activeDateGroup.items.length === 1 ? "" : "s"}</p>
                            </div>
                            <div className="hr-queue-modal-actions">
                                {["all", "approved", "pending"].map((filter) => (
                                    <button
                                        type="button"
                                        key={filter}
                                        className={modalStatusFilter === filter ? "active" : ""}
                                        onClick={() => setModalStatusFilter(filter)}
                                    >
                                        {filter}
                                    </button>
                                ))}
                                <button type="button" className="close" onClick={() => setActiveDateGroup(null)} aria-label="Close details">
                                    <X size={18} />
                                </button>
                            </div>
                        </div>
                        <div className="hr-queue-modal-items">
                            {visibleModalItems.length ? visibleModalItems.map((item) => (
                                <HrRecentItem
                                    key={`${item.type}-${item._id}`}
                                    item={item}
                                    dense
                                    showCorrectionAction={isCorrectionQueue}
                                    onNotifyAdmin={onNotifyAdmin}
                                />
                            )) : (
                                <p className="hr-queue-modal-empty">No {modalStatusFilter} items for this date.</p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const HrRecentItem = ({ item, dense = false, showCorrectionAction = false, onNotifyAdmin }) => {
    const isTitleSameAsReason = useMemo(() => {
        if (!item.title || !item.reason) return false;
        const cleanTitle = item.title.replace(/\s+/g, "").toLowerCase();
        const cleanReason = item.reason.replace(/\s+/g, "").toLowerCase();
        return cleanReason.includes(cleanTitle) || cleanTitle.includes(cleanReason);
    }, [item.title, item.reason]);

    return (
        <div className={`hr-monitor-recent-item ${dense ? "dense" : ""}`}>
            <div className="hr-recent-main">
                <div className="hr-recent-type-row">
                    <span>{item.type}</span>
                    <em>{formatQueueStatus(item.status)}</em>
                </div>
                {!isTitleSameAsReason && <strong>{item.title || "Employee request"}</strong>}
                <small>
                    {item.employee?.name || "Employee"} {item.employee?.employeeId ? `- ${item.employee.employeeId}` : ""}
                    {item.date ? ` - ${formatShortDate(item.date)}` : ""}
                </small>
                {item.reason && (
                    <div className="hr-queue-item-reason">
                        <span>Reason</span>
                        <p>{formatReasonText(item.reason)}</p>
                    </div>
                )}
            </div>
            <div className="hr-queue-item-status">
                {(isApprovedQueueStatus(item.status) || isRejectedQueueStatus(item.status)) && (
                    <small>
                        {isApprovedQueueStatus(item.status) ? "Approved" : "Rejected"} by Super Admin
                    </small>
                )}
                {showCorrectionAction && ["pending", "pending_lead"].includes(item.status) && (
                    <button type="button" onClick={() => onNotifyAdmin?.(item)}>
                        {item.hrNotifiedAdminAt ? "Message again" : "Message admin"}
                    </button>
                )}
                {showCorrectionAction && item.status === "pending_lead" && (
                    <small>Lead review pending</small>
                )}
            </div>
        </div>
    );
};

const MissingEodPanel = ({
    count,
    items = [],
    loading,
    mode,
    startDate,
    endDate,
    range = {},
    onModeChange,
    onStartDateChange,
    onEndDateChange,
    onRefresh,
}) => {
    const [showMissingEodModal, setShowMissingEodModal] = useState(false);
    const dateLabel = mode === "today"
        ? `Today - ${formatShortDate(getDateKey())}`
        : range.startDate && range.endDate
            ? `${formatShortDate(range.startDate)}${range.startDate !== range.endDate ? ` - ${formatShortDate(range.endDate)}` : ""}`
            : "Today";

    const stopControlClick = (event) => event.stopPropagation();

    return (
        <>
            <section
                className="premium-card hr-side-panel hr-missing-eod-panel"
                role="button"
                tabIndex={0}
                onClick={() => setShowMissingEodModal(true)}
                onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setShowMissingEodModal(true);
                    }
                }}
            >
                <div className="premium-card-header compact">
                    <div>
                        <span className="premium-section-kicker">EOD tracking</span>
                        <h2>Missing EOD</h2>
                        <p className="hr-missing-eod-subtitle">{dateLabel}</p>
                        <p className="hr-missing-eod-note">Tap to view checked-in employees without EOD</p>
                    </div>
                    <strong className="hr-missing-eod-count">{loading ? "..." : count}</strong>
                </div>

                <div className="hr-missing-eod-controls" onClick={stopControlClick}>
                    <div className="hr-missing-eod-tabs">
                        <button type="button" className={mode === "today" ? "active" : ""} onClick={() => onModeChange("today")}>
                            Today
                        </button>
                        <button type="button" className={mode === "custom" ? "active" : ""} onClick={() => onModeChange("custom")}>
                            Custom range
                        </button>
                    </div>
                    {mode === "custom" && (
                        <div className="hr-missing-eod-date-grid">
                            <label>
                                <span>From</span>
                                <input type="date" value={startDate} onChange={(event) => onStartDateChange(event.target.value)} />
                            </label>
                            <label>
                                <span>To</span>
                                <input type="date" value={endDate} onChange={(event) => onEndDateChange(event.target.value)} />
                            </label>
                            <button type="button" onClick={onRefresh}>Apply</button>
                        </div>
                    )}
                </div>

                <div className="hr-missing-eod-summary">
                    <strong>{loading ? "Checking reports..." : `${count} missing report${count === 1 ? "" : "s"}`}</strong>
                    <small>Touch this card to open the employee list</small>
                </div>
            </section>

            {showMissingEodModal && (
                <div className="manufacturing-modal-backdrop" role="dialog" aria-modal="true" aria-label="Missing EOD reports">
                    <section className="hr-missing-eod-modal">
                        <div className="hr-missing-eod-modal-head">
                            <div>
                                <span className="premium-section-kicker">EOD tracking</span>
                                <h2>Missing EOD</h2>
                                <p>{dateLabel} · {count} report{count === 1 ? "" : "s"}</p>
                            </div>
                            <button type="button" className="manufacturing-icon-button" onClick={() => setShowMissingEodModal(false)} aria-label="Close Missing EOD">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="hr-missing-eod-list modal-list">
                            {loading ? (
                                <p className="premium-empty-text">Checking missing EOD reports...</p>
                            ) : items.length ? items.map((item) => (
                                <article key={item._id} className="hr-missing-eod-row">
                                    <div>
                                        <strong>{item.employee?.name || "Employee"}</strong>
                                        <small>{item.employee?.employeeId || "-"}{item.employee?.team ? ` - ${formatTeamLabel(item.employee.team)}` : ""}</small>
                                    </div>
                                    <div className="hr-missing-eod-dates">
                                        {(mode === "today" ? [getDateKey()] : item.missingDates || []).map((date) => (
                                            <span key={date}>{formatShortDate(date)}</span>
                                        ))}
                                    </div>
                                </article>
                            )) : (
                                <p className="hr-missing-eod-clear">No missing EOD reports for this date selection.</p>
                            )}
                        </div>
                    </section>
                </div>
            )}
        </>
    );
};

const HrTeamDistribution = ({ teams = [], total = 0 }) => (
    <div className="hr-team-distribution">
        {teams.length ? teams.slice(0, 6).map((team) => {
            const percent = total > 0 ? Math.round((Number(team.count || 0) / total) * 100) : 0;
            return (
                <div key={team.team} className="hr-team-row">
                    <div>
                        <span>{team.team}</span>
                        <strong>{team.count}</strong>
                    </div>
                    <div className="hr-team-track">
                        <span style={{ width: `${Math.max(4, percent)}%` }} />
                    </div>
                </div>
            );
        }) : (
            <p className="premium-empty-text">No team data available.</p>
        )}
    </div>
);

const HrEmployeeRow = ({ employee, onOpen }) => (
    <button
        type="button"
        className="hr-employee-row"
        onClick={(event) => {
            event.stopPropagation();
            onOpen();
        }}
    >
        <span>{String(employee?.name || "E").slice(0, 1).toUpperCase()}</span>
        <div>
            <strong>{employee?.name || "Employee"}</strong>
            <small>
                {employee?.employeeId || "No ID"} {employee?.team ? `- ${formatTeamLabel(employee.team)}` : ""}
            </small>
        </div>
        <em className={employee?.isActive ? "active" : ""}>{employee?.status || "Active"}</em>
    </button>
);

const HrEmployeePreview = ({ employees = [] }) => (
    <div className="hr-employee-preview">
        <div className="hr-employee-preview-chips">
            {employees.slice(0, 8).map((employee) => (
                <span key={employee._id} title={employee.name || "Employee"}>
                    {String(employee?.name || "E").slice(0, 1).toUpperCase()}
                </span>
            ))}
        </div>
        <div className="hr-employee-preview-copy">
            <strong>{employees.length} recent employee{employees.length === 1 ? "" : "s"}</strong>
            <small>Tap to view full employee details</small>
        </div>
    </div>
);

const HrEmployeeDirectoryModal = ({ employees = [], onClose, onSelect }) => (
    <div className="manufacturing-modal-backdrop" role="dialog" aria-modal="true" aria-label="Recent employees">
        <section className="hr-directory-modal">
            <div className="hr-employee-modal-head">
                <span>
                    <UserRoundCheck size={24} />
                </span>
                <div>
                    <small>Directory</small>
                    <h2>Recent employees</h2>
                    <p>{employees.length} employee{employees.length === 1 ? "" : "s"} shown</p>
                </div>
                <button type="button" className="manufacturing-icon-button" onClick={onClose} aria-label="Close recent employees">
                    <X size={18} />
                </button>
            </div>

            <div className="hr-directory-list">
                {employees.length ? employees.map((employee) => (
                    <HrEmployeeRow key={employee._id} employee={employee} onOpen={() => onSelect(employee)} />
                )) : (
                    <p className="premium-empty-text">No employee records found.</p>
                )}
            </div>
        </section>
    </div>
);

const HrEmployeeDetailsModal = ({ employee, onClose }) => (
    <div className="manufacturing-modal-backdrop hr-employee-detail-backdrop" role="dialog" aria-modal="true" aria-label="Employee details">
        <section className="hr-employee-modal">
            <div className="hr-employee-modal-head">
                <span>{String(employee?.name || "E").slice(0, 1).toUpperCase()}</span>
                <div>
                    <small>Employee profile</small>
                    <h2>{employee?.name || "Employee"}</h2>
                    <p>{employee?.employeeId || "No employee ID"}</p>
                </div>
                <em className={employee?.isActive ? "active" : ""}>{employee?.status || "Active"}</em>
                <button type="button" className="manufacturing-icon-button" onClick={onClose} aria-label="Close employee details">
                    <X size={18} />
                </button>
            </div>

            <div className="hr-employee-detail-grid">
                <MiniMetric label="Team" value={formatTeamLabel(employee?.team) || "-"} />
                <MiniMetric label="Designation" value={employee?.designation || "-"} />
                <MiniMetric label="Email" value={employee?.email || "-"} />
                <MiniMetric label="Status" value={employee?.status || (employee?.isActive ? "Active" : "Inactive")} />
            </div>
        </section>
    </div>
);

const TodayChecklist = ({
    attendanceStatus,
    record,
    todayEOD,
    pendingTasks,
    unreadAnnouncements,
    onAttendance,
    onEod,
    onTasks,
    onAnnouncements,
}) => {
    const checkedIn = Boolean(record?.checkIn);
    const lunchDone = Boolean(record?.breakEnd || (record?.breaks || []).some((item) => item.type === "lunch" && item.endTime));
    const eodDone = todayEOD?.status === "submitted";

    const items = [
        {
            label: "Check-in",
            detail: checkedIn ? "Done" : "Not yet",
            done: checkedIn,
            onClick: onAttendance,
        },
        {
            label: "Lunch",
            detail: attendanceStatus?.status === "on_break" ? "Running" : lunchDone ? "Done" : "Pending",
            done: lunchDone,
            onClick: onAttendance,
        },
        {
            label: "EOD",
            detail: eodDone ? "Submitted" : todayEOD?.status === "draft" ? "Draft" : "Pending",
            done: eodDone,
            onClick: onEod,
        },
        {
            label: "Tasks",
            detail: `${pendingTasks} pending`,
            done: pendingTasks === 0,
            onClick: onTasks,
        },
        {
            label: "Announcements",
            detail: `${unreadAnnouncements} unread`,
            done: unreadAnnouncements === 0,
            onClick: onAnnouncements,
        },
    ];

    return (
        <section className="premium-card mt-5">
            <div className="premium-card-header compact">
                <div>
                    <span className="premium-section-kicker">Today checklist</span>
                    <h2>Close the day cleanly</h2>
                </div>
                <CheckCircle2 size={18} />
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5 mt-4">
                {items.map((item) => (
                    <button
                        key={item.label}
                        type="button"
                        onClick={item.onClick}
                        className="min-h-[82px] rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-3 text-left transition hover:border-[var(--accent-primary)]"
                    >
                        <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full ${item.done ? "bg-emerald-500 text-white" : "bg-amber-100 text-amber-700"}`}>
                            {item.done ? <CheckCircle2 size={15} /> : <Clock3 size={15} />}
                        </span>
                        <strong className="mt-2 block text-sm">{item.label}</strong>
                        <span className="text-xs text-[var(--text-secondary)]">{item.detail}</span>
                    </button>
                ))}
            </div>
        </section>
    );
};

const badgeToneClass = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    violet: "border-violet-200 bg-violet-50 text-violet-700",
    blue: "border-blue-200 bg-blue-50 text-blue-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
};

const badgeVisuals = {
    on_time_streak: {
        label: "Punctuality",
        icon: <Clock3 size={22} />,
        frame: "border-emerald-200 bg-emerald-50 text-emerald-700",
        hero: "from-emerald-500 to-teal-500 text-white",
        glow: "bg-emerald-400/20",
    },
    eod_champion: {
        label: "Reporting",
        icon: <FileText size={22} />,
        frame: "border-violet-200 bg-violet-50 text-violet-700",
        hero: "from-violet-600 to-fuchsia-500 text-white",
        glow: "bg-violet-400/20",
    },
    top_task_finisher: {
        label: "Delivery",
        icon: <CheckCircle2 size={22} />,
        frame: "border-blue-200 bg-blue-50 text-blue-700",
        hero: "from-blue-600 to-cyan-500 text-white",
        glow: "bg-blue-400/20",
    },
    zero_lop_month: {
        label: "Attendance",
        icon: <Award size={22} />,
        frame: "border-amber-200 bg-amber-50 text-amber-700",
        hero: "from-amber-500 to-orange-500 text-white",
        glow: "bg-amber-400/25",
    },
};

const getBadgeVisual = (badge) => badgeVisuals[badge?.key] || {
    label: "Recognition",
    icon: <Award size={22} />,
    frame: badgeToneClass[badge?.tone] || badgeToneClass.blue,
    hero: "from-slate-700 to-violet-600 text-white",
    glow: "bg-violet-400/20",
};

const RecognitionBadge = ({ badge, onClick }) => (
    (() => {
        const visual = getBadgeVisual(badge);

        return (
            <button
                type="button"
                className={`w-full rounded-xl border px-3 py-3 text-left transition-all hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-violet-500/40 ${badgeToneClass[badge.tone] || badgeToneClass.blue}`}
                onClick={onClick}
                aria-label={`View ${badge.title} badge`}
            >
                <div className="flex min-h-10 items-center gap-3">
                    <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border bg-white/75 ${visual.frame}`}>
                        {React.cloneElement(visual.icon, { size: 18 })}
                    </span>
                    <span className="min-w-0 flex-1">
                        <span className="mb-1 inline-flex rounded-full bg-white/65 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide opacity-90">
                            {visual.label}
                        </span>
                        <strong className="block text-sm font-black">{badge.title}</strong>
                        <span className="mt-0.5 block text-xs font-bold opacity-80">{badge.description}</span>
                    </span>
                </div>
            </button>
        );
    })()
);

const BadgeDetailsModal = ({ badge, onClose }) => {
    useEffect(() => {
        if (!badge) return undefined;

        const handleEscape = (event) => {
            if (event.key === "Escape") onClose();
        };

        window.addEventListener("keydown", handleEscape);
        return () => window.removeEventListener("keydown", handleEscape);
    }, [badge, onClose]);

    if (!badge) return null;

    const visual = getBadgeVisual(badge);
    const score = Number(badge.score || 0);

    return (
        <div className="fixed inset-0 z-[220] flex items-center justify-center px-4 py-6">
            <button
                type="button"
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={onClose}
                aria-label="Close badge details"
            />

            <section className="relative w-full max-w-md overflow-hidden rounded-3xl border border-white/70 bg-white p-6 shadow-2xl dark:border-neutral-800 dark:bg-neutral-950">
                <div className={`absolute right-10 top-10 h-28 w-28 rounded-full blur-3xl ${visual.glow}`} />
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-xl bg-neutral-100 text-neutral-700 transition hover:bg-neutral-200 dark:bg-neutral-900 dark:text-neutral-200 dark:hover:bg-neutral-800"
                    aria-label="Close"
                >
                    <X size={18} />
                </button>

                <div className="relative">
                    <div className={`flex h-24 w-24 items-center justify-center rounded-[28px] bg-gradient-to-br shadow-xl ${visual.hero}`}>
                        {React.cloneElement(visual.icon, { size: 38, strokeWidth: 2.4 })}
                    </div>
                    <span className="absolute -bottom-2 left-1/2 inline-flex -translate-x-1/2 items-center rounded-full border border-white bg-white px-3 py-1 text-[10px] font-black uppercase tracking-wide text-neutral-800 shadow-md dark:border-neutral-800 dark:bg-neutral-900 dark:text-white">
                        {visual.label}
                    </span>
                </div>

                <span className="mt-5 block text-[11px] font-black uppercase tracking-[0.18em] text-violet-700">
                    Recognition Badge
                </span>
                <h3 className="mt-2 pr-10 text-2xl font-black leading-tight text-neutral-950 dark:text-white">
                    {badge.title}
                </h3>
                <p className="mt-3 text-sm font-semibold leading-6 text-neutral-600 dark:text-neutral-300">
                    {badge.description}
                </p>

                <div className="mt-5 grid grid-cols-2 gap-3">
                    <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4 dark:border-neutral-800 dark:bg-neutral-900/70">
                        <span className="block text-[11px] font-black uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                            Status
                        </span>
                        <strong className="mt-1 block text-sm font-black text-neutral-950 dark:text-white">
                            Earned this month
                        </strong>
                    </div>
                    <div className={`rounded-2xl border p-4 ${visual.frame}`}>
                        <span className="block text-[11px] font-black uppercase tracking-wide opacity-75">
                            Badge Score
                        </span>
                        <strong className="mt-1 block text-sm font-black">
                            {score > 0 ? score : "Active"}
                        </strong>
                    </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                    {Object.entries(badgeVisuals).map(([key, item]) => (
                        <span
                            key={key}
                            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-wide ${key === badge.key ? item.frame : "border-neutral-200 bg-neutral-50 text-neutral-500 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-400"}`}
                        >
                            {React.cloneElement(item.icon, { size: 12 })}
                            {item.label}
                        </span>
                    ))}
                </div>
            </section>
        </div>
    );
};

const QuickActionButton = ({ icon, label, badge, onClick }) => (
    <button
        type="button"
        className="premium-quick-action"
        onClick={onClick}
    >
        <span className="premium-quick-icon">
            {icon}
        </span>
        <span className="premium-quick-copy">
            <span>{label}</span>
            {badge !== undefined && badge !== null && (
                <em>{badge}</em>
            )}
        </span>
        <ArrowRight size={15} className="premium-quick-arrow" />
    </button>
);

const TimelineItem = ({ label, value }) => (
    <div className="premium-timeline-item">
        <span>{label}</span>
        <strong>{value}</strong>
    </div>
);

const AssetRow = ({ asset }) => (
    <div className="premium-asset-row">
        <div className="premium-asset-icon">
            <Package size={16} />
        </div>
        <div className="premium-asset-copy">
            <strong>{asset.assetName || "Unnamed Asset"}</strong>
            <span>
                {asset.assetType || "Asset"}
                {asset.serialNumber ? ` - ${asset.serialNumber}` : ""}
            </span>
        </div>
        <span className="premium-asset-badge">{asset.status || "Assigned"}</span>
    </div>
);

const AnnouncementCard = ({ announcement }) => (
    <article className={`premium-announcement ${announcement.isRead ? "" : "unread"}`}>
        <div>
            <strong>{announcement.title}</strong>
            <span>
                {announcement.category || "general"} -{" "}
                {new Date(announcement.createdAt).toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                })}
            </span>
        </div>
        <BellRing size={16} />
    </article>
);

const StatCard = ({ label, value, icon, tone, onClick }) => (
    <button className={`premium-stat-card ${tone}`} onClick={onClick}>
        <span className="premium-stat-icon">{icon}</span>
        <span className="premium-stat-label">{label}</span>
        <strong>{value}</strong>
    </button>
);

export default Dashboard;
